/**
 * StewardThinking — the brand mark, doing the work.
 *
 * WHY IT EXISTS. A Steward turn is genuinely slow: the candidate pass, then
 * SSRF-guarded page fetches, then — for stays — a real headless browser driving
 * Google's date picker, serialized process-wide. Tens of seconds is normal and
 * CORRECT. What was not correct was showing a 3mm generic spinner for all of it,
 * which reads as "hung" long before it reads as "researching".
 *
 * So this says three things a plain spinner cannot:
 *   1. WHOSE work it is — it is the TripMuscle dumbbell, the actual brand mark
 *      from BrandLockup, with a teal charge sweeping along the bar. The mark
 *      loads. Nothing else in the app does that, so it is unmistakably Steward.
 *   2. WHAT she is doing right now — the live phase line from the server
 *      ("Pulling live rates for 2 stays"), not a euphemism.
 *   3. THAT A LONG WAIT IS EXPECTED — past a threshold it explains itself
 *      rather than letting the traveler wonder whether to reload.
 *
 * `prefers-reduced-motion` is respected throughout: the mark holds still and
 * fully charged, and the copy carries the whole message. Motion is the garnish,
 * never the information.
 */
import { useEffect, useState } from "react";

/** Past this, silence starts to read as breakage — so we explain ourselves. */
const REASSURE_AFTER_MS = 12_000;

interface Props {
  /** The server's live phase line. Falls back to a neutral working label. */
  status?: string | null;
  /** Test seam: pretend this many ms have already elapsed. */
  initialElapsedMs?: number;
}

export function reassurance(
  elapsedMs: number,
  status?: string | null,
): string | null {
  if (elapsedMs < REASSURE_AFTER_MS) return null;
  // Rate lookups are the slow phase and the one worth explaining — the traveler
  // is waiting on a real browser loading a real listing, not on our server.
  return /rate/i.test(status ?? "")
    ? "Live rates come from a real search, so this one takes a moment."
    : "Still going — she reads the actual pages rather than guessing.";
}

export default function StewardThinking({
  status,
  initialElapsedMs = 0,
}: Props) {
  const [elapsed, setElapsed] = useState(initialElapsedMs);

  useEffect(() => {
    const started = Date.now() - initialElapsedMs;
    const t = setInterval(() => setElapsed(Date.now() - started), 1000);
    return () => clearInterval(t);
  }, [initialElapsedMs]);

  const note = reassurance(elapsed, status);

  return (
    <>
      {/* Outside the role="status" element on purpose: anything inside a live
          region is part of its accessible subtree, and a screen reader has no
          business reading out keyframes. */}
      <style>{`
        @keyframes tm-charge {
          0%   { stroke-dashoffset: 12; }
          55%  { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes tm-plate {
          0%, 100% { opacity: .35; transform: scaleY(.82); }
          50%      { opacity: 1;   transform: scaleY(1); }
        }
        .tm-bar   { stroke-dasharray: 12; animation: tm-charge 1.6s ease-in-out infinite; }
        .tm-plate { transform-origin: center; animation: tm-plate 1.6s ease-in-out infinite; }
        .tm-p2 { animation-delay: .12s; }
        .tm-p3 { animation-delay: .24s; }
        .tm-p4 { animation-delay: .36s; }
        @media (prefers-reduced-motion: reduce) {
          .tm-bar, .tm-plate { animation: none; stroke-dashoffset: 0; opacity: 1; transform: none; }
        }
      `}</style>

      <div
        className="flex items-start gap-2.5"
        role="status"
        aria-live="polite"
        aria-label={
          status ? `Your Steward is working: ${status}` : "Your Steward is working"
        }
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="mt-0.5 h-5 w-5 flex-none text-[hsl(var(--primary))]"
        >
          {/* Outer plates, then inner plates — they breathe in a stagger, so the
            mark looks like it is lifting rather than blinking. */}
          <line className="tm-plate" x1="3" y1="9" x2="3" y2="15" />
          <line
            className="tm-plate tm-p2"
            x1="6.5"
            y1="6.5"
            x2="6.5"
            y2="17.5"
          />
          <line
            className="tm-plate tm-p3"
            x1="17.5"
            y1="6.5"
            x2="17.5"
            y2="17.5"
          />
          <line className="tm-plate tm-p4" x1="21" y1="9" x2="21" y2="15" />
          {/* The bar charges left→right: the actual progress metaphor. */}
          <line className="tm-bar" x1="6.5" y1="12" x2="17.5" y2="12" />
        </svg>

        <div className="min-w-0 space-y-0.5 pt-0.5">
          <p className="text-xs font-medium text-foreground">
            {status || "Your Steward is working on it"}
            <span className="ml-1 font-normal text-muted-foreground">
              {elapsed >= 3000 ? `· ${Math.floor(elapsed / 1000)}s` : ""}
            </span>
          </p>
          {note && (
            <p className="text-[11px] leading-snug text-muted-foreground">
              {note}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
